import type { FC } from "react";

import { startOfDay, startOfMonth, startOfQuarter, startOfWeek, startOfYear, subDays } from "date-fns";

import type { DateTimePickerProps } from "../dateTimePicker";

import { Button, ButtonColor } from "../button";
import { DateTimePicker } from "../dateTimePicker";
import { Label } from "../label";
import style from "./style.module.scss";

interface Props extends DateTimePickerProps {
	id: string;
	label: string;
	className?: string;
	value: [Date | undefined, Date | undefined];
	setValue: ([start, end]: [Date | undefined, Date | undefined]) => void;

	/**
	 * Show the quick selection options to select range
	 * Options are:
	 *  - Day
	 *  - Week
	 *  - Month
	 *  - Quarter
	 *  - All
	 */
	quickRange?: boolean;
	quickRangeOptions?: QuickRangeOptions[];
	/** Fired when one of the quick-range buttons is clicked, in addition to `setValue`. */
	onQuickRangeSelected?: (value: [Date | undefined, Date | undefined]) => void;
}

type QuickRangeOptions = "today" | "yesterday" | "week" | "month" | "quarter" | "year" | "all";

export const DateTimeRangePicker: FC<Props> = (props) => {
	const {
		setValue,
		id,
		label,
		value,
		quickRange,
		quickRangeOptions = ["today", "week", "month", "quarter", "all"],
		dateOnly = true,
		onQuickRangeSelected,
		...rest
	} = props;

	function quickRangeClicked(value: [Date | undefined, Date | undefined]) {
		setValue(value);
		onQuickRangeSelected?.(value);
	}

	function todayClicked() {
		quickRangeClicked([startOfDay(new Date()), startOfDay(new Date())]);
	}

	function yesterdayClicked() {
		const yesterday = subDays(new Date(), 1);

		quickRangeClicked([startOfDay(yesterday), startOfDay(yesterday)]);
	}

	function weekClicked() {
		quickRangeClicked([startOfWeek(new Date()), startOfDay(new Date())]);
	}

	function monthClicked() {
		quickRangeClicked([startOfMonth(new Date()), startOfDay(new Date())]);
	}

	function quarterClicked() {
		quickRangeClicked([startOfQuarter(new Date()), startOfDay(new Date())]);
	}

	function yearClicked() {
		quickRangeClicked([startOfYear(new Date()), startOfDay(new Date())]);
	}

	function allClicked() {
		quickRangeClicked([undefined, undefined]);
	}

	return (
		<div className={props.className}>
			<Label className={style.controlLabel}>{label}</Label>
			<div className={style.root}>
				<DateTimePicker
					{...rest}
					dateOnly={dateOnly}
					id={`${id}-start`}
					label=""
					value={value[0]}
					changeHandler={(v) => setValue([v as Date, value[1]])}
				/>
				<DateTimePicker
					{...rest}
					dateOnly={dateOnly}
					id={`${id}-end`}
					label=""
					value={value[1]}
					changeHandler={(v) => setValue([value[0], v as Date])}
					className={style.input}
				/>
			</div>
			{quickRange ? (
				<div className={style.buttons}>
					<Button
						onClick={todayClicked}
						color={ButtonColor.Green}
						hidden={!quickRangeOptions.includes("today")}
					>
						Today
					</Button>
					<Button
						onClick={yesterdayClicked}
						color={ButtonColor.Green}
						hidden={!quickRangeOptions.includes("yesterday")}
					>
						Yesterday
					</Button>
					<Button
						onClick={weekClicked}
						color={ButtonColor.Green}
						hidden={!quickRangeOptions.includes("week")}
					>
						Week
					</Button>
					<Button
						onClick={monthClicked}
						color={ButtonColor.Green}
						hidden={!quickRangeOptions.includes("month")}
					>
						Month
					</Button>
					<Button
						onClick={quarterClicked}
						color={ButtonColor.Green}
						hidden={!quickRangeOptions.includes("quarter")}
					>
						Quarter
					</Button>
					<Button
						onClick={yearClicked}
						color={ButtonColor.Green}
						hidden={!quickRangeOptions.includes("year")}
					>
						Year
					</Button>
					<Button onClick={allClicked} color={ButtonColor.Green} hidden={!quickRangeOptions.includes("all")}>
						All
					</Button>
				</div>
			) : null}
		</div>
	);
};
